import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, logout } from "../utils/auth";
import "./ReportMCQListPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const ITEMS_PER_PAGE = 8;

export default function ReportMCQListPage() {
  const [mcqs, setMcqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [sortType, setSortType] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedId, setCopiedId] = useState(null);

  const navigate = useNavigate();

  /* ================= API CALL ================= */
  const fetchMCQs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/mcqs/getallmcq`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
      });

      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login");
        return;
      }

      const data = await res.json();
      setMcqs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch MCQ datasets:", error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchMCQs();
  }, [fetchMCQs]);

  /* ================= CATEGORIES ================= */
  const categories = useMemo(() => {
    const unique = new Set(mcqs.map((m) => m.category).filter(Boolean));
    return ["All", ...Array.from(unique)];
  }, [mcqs]);

  /* ================= AGGREGATED METRICS ================= */
  const metrics = useMemo(() => {
    const totalSets = mcqs.length;
    const totalQuestions = mcqs.reduce(
      (acc, m) => acc + (m.questions?.length || 0),
      0
    );
    const avgQuestions = totalSets ? (totalQuestions / totalSets).toFixed(1) : 0;
    return { totalSets, totalQuestions, avgQuestions };
  }, [mcqs]);

  /* ================= FILTERING & SORTING ================= */
  const processedMCQs = useMemo(() => {
    let list = mcqs.filter((item) => {
      const matchesSearch = item.topic
        ?.toLowerCase()
        .includes(search.toLowerCase().trim());
      const matchesCategory =
        categoryFilter === "All" || item.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });

    return list.sort((a, b) => {
      if (sortType === "az") return (a.topic || "").localeCompare(b.topic || "");
      if (sortType === "za") return (b.topic || "").localeCompare(a.topic || "");
      if (sortType === "questions") {
        return (b.questions?.length || 0) - (a.questions?.length || 0);
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [mcqs, search, categoryFilter, sortType]);

  /* Reset pagination on filter change */
  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, sortType]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(processedMCQs.length / ITEMS_PER_PAGE) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return processedMCQs.slice(start, start + ITEMS_PER_PAGE);
  }, [processedMCQs, currentPage]);

  const handleCopyLink = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/admin/reports/mcq/${id}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="pro-dashboard">
      {/* PAGE HEADER */}
      <header className="pro-header">
        <div className="pro-header-meta">
          <nav className="pro-breadcrumb">Analytics / MCQ Reports</nav>
          <h1 className="pro-title">Assessment Repository</h1>
          <p className="pro-desc">
            Monitor and audit question distribution, categories, and test analytics.
          </p>
        </div>
        <div className="pro-header-actions">
          <button className="btn-secondary" onClick={fetchMCQs} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh Data"}
          </button>
        </div>
      </header>

      {/* METRIC KPI TILES */}
      <section className="pro-kpi-grid">
        <div className="kpi-card">
          <span className="kpi-label">Active Modules</span>
          <div className="kpi-row">
            <span className="kpi-value">{metrics.totalSets}</span>
            <span className="kpi-badge neutral">Total</span>
          </div>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Aggregated Questions</span>
          <div className="kpi-row">
            <span className="kpi-value">{metrics.totalQuestions}</span>
            <span className="kpi-badge positive">Published</span>
          </div>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Mean Questions / Module</span>
          <div className="kpi-row">
            <span className="kpi-value">{metrics.avgQuestions}</span>
            <span className="kpi-badge highlight">Avg</span>
          </div>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Domain Classifications</span>
          <div className="kpi-row">
            <span className="kpi-value">{categories.length - 1}</span>
            <span className="kpi-badge neutral">Unique</span>
          </div>
        </div>
      </section>

      {/* CONTROLS & FILTER BAR */}
      <section className="pro-table-wrapper">
        <div className="pro-filter-bar">
          <div className="search-wrapper">
            <svg className="search-svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            <input
              type="text"
              className="pro-input"
              placeholder="Search by topic title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="clear-btn" onClick={() => setSearch("")}>×</button>
            )}
          </div>

          <div className="select-group">
            <select
              className="pro-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>Category: {cat}</option>
              ))}
            </select>

            <select
              className="pro-select"
              value={sortType}
              onChange={(e) => setSortType(e.target.value)}
            >
              <option value="latest">Sort: Newest First</option>
              <option value="az">Sort: Topic (A to Z)</option>
              <option value="za">Sort: Topic (Z to A)</option>
              <option value="questions">Sort: Question Volume</option>
            </select>
          </div>
        </div>

        {/* DATA TABLE */}
        <div className="pro-table-container">
          <table className="pro-table">
            <thead>
              <tr>
                <th style={{ width: "35%" }}>Topic & Specification</th>
                <th style={{ width: "20%" }}>Category</th>
                <th style={{ width: "15%" }}>Volume</th>
                <th style={{ width: "15%" }}>Created Date</th>
                <th style={{ width: "15%", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="state-cell">
                    <div className="table-spinner"></div>
                    <p>Loading database records...</p>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="state-cell">
                    <p className="no-data-title">No matching reports found</p>
                    <p className="no-data-sub">Try refining or resetting your active query parameters.</p>
                  </td>
                </tr>
              ) : (
                paginatedData.map((mcq) => (
                  <tr
                    key={mcq._id}
                    className="pro-row"
                    onClick={() => navigate(`/admin/reports/mcq/${mcq._id}`)}
                  >
                    <td>
                      <div className="cell-topic">
                        <span className="topic-primary">{mcq.topic}</span>
                        <span className="topic-id">ID: {mcq._id.slice(-8)}</span>
                      </div>
                    </td>
                    <td>
                      <span className="badge category-badge">
                        {mcq.category || "Uncategorized"}
                      </span>
                    </td>
                    <td>
                      <span className="badge count-badge">
                        {mcq.questions?.length || 0} Questions
                      </span>
                    </td>
                    <td className="cell-muted">{formatDate(mcq.createdAt)}</td>
                    <td style={{ textAlign: "right" }}>
                      <div className="actions-cluster" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn-action"
                          title="Copy Link"
                          onClick={(e) => handleCopyLink(e, mcq._id)}
                        >
                          {copiedId === mcq._id ? "Copied" : "Copy"}
                        </button>
                        <button
                          className="btn-action primary"
                          onClick={() => navigate(`/admin/reports/mcq/${mcq._id}`)}
                        >
                          Audit →
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION FOOTER */}
        {!loading && processedMCQs.length > 0 && (
          <footer className="pro-pagination">
            <span className="pagination-info">
              Showing{" "}
              <strong>{(currentPage - 1) * ITEMS_PER_PAGE + 1}</strong>–
              <strong>
                {Math.min(currentPage * ITEMS_PER_PAGE, processedMCQs.length)}
              </strong>{" "}
              of <strong>{processedMCQs.length}</strong> entries
            </span>
            <div className="pagination-controls">
              <button
                className="btn-page"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              >
                Previous
              </button>
              <div className="page-indicator">
                {currentPage} / {totalPages}
              </div>
              <button
                className="btn-page"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              >
                Next
              </button>
            </div>
          </footer>
        )}
      </section>
    </div>
  );
}